// components/layout/AppLayout.js
// Layout wrapper — Sidebar + Header + Main content
//
// Responsive:
// - Desktop >1200px: Sidebar 260px, full content
// - Tablet 768-1200px: Sidebar 60px (icons only)
// - Mobile <768px: No sidebar, hamburger menu
//
// Background: theme image/gradient with overlay
// Includes: AccessibilityWidget, chat placeholder

import { useState, useCallback, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Box, useTheme, useMediaQuery } from '@mui/material';

import Sidebar, { SIDEBAR_FULL_WIDTH, SIDEBAR_ICON_WIDTH } from './Sidebar';
import Header from './Header';
import { AccessibilityWidget } from '../shared';
import { setupTestHarness, setNavigateRef } from '../../services/testHarness';

// =============================================================================
// AppLayout Component
// =============================================================================
export default function AppLayout() {
  const theme = useTheme();
  const navigate = useNavigate();

  // Responsive
  const isDesktop = useMediaQuery('(min-width:1200px)');
  const isTablet = useMediaQuery('(min-width:768px) and (max-width:1199px)');
  const isMobile = useMediaQuery('(max-width:767px)');

  // Mobile sidebar state
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleMobileToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const handleMobileClose = useCallback(() => {
    setMobileOpen(false);
  }, []);

  // Current sidebar width
  const sidebarWidth = isMobile ? 0 : isTablet ? SIDEBAR_ICON_WIDTH : SIDEBAR_FULL_WIDTH;

  // Register navigate ref for test harness
  useEffect(() => {
    setNavigateRef(navigate);
    setupTestHarness();
  }, [navigate]);

  // ===================================================================
  // Background styles from theme
  // ===================================================================
  const customTheme = theme.custom || {};
  const bgStyles = {};

  if (customTheme.hasBackgroundImage) {
    // Theme with background image — URL with gradient fallback
    // CSS background shorthand: image falls back to gradient if URL fails to load
    bgStyles.background = `url(${customTheme.backgroundImage}) center/cover fixed no-repeat`
      + (customTheme.backgroundFallback ? `, ${customTheme.backgroundFallback}` : '');
  } else if (customTheme.backgroundGradient) {
    // Clean theme — gradient + optional pattern
    bgStyles.background = customTheme.backgroundGradient;
  }

  return (
    <Box
      data-testid="layout__root"
      sx={{
        display: 'flex',
        minHeight: '100vh',
        direction: 'rtl',
        ...bgStyles,
      }}
    >
      {/* Background image overlay */}
      {customTheme.hasBackgroundImage && customTheme.overlayColor && (
        <Box
          data-testid="layout__overlay"
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: customTheme.overlayColor,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Background pattern for clean themes */}
      {!customTheme.hasBackgroundImage && customTheme.backgroundPattern && customTheme.backgroundPattern !== 'none' && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            backgroundImage: customTheme.backgroundPattern,
            backgroundSize: customTheme.backgroundPatternSize || '32px 32px',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Sidebar */}
      <Sidebar mobileOpen={mobileOpen} onMobileClose={handleMobileClose} />

      {/* Main area */}
      <Box
        component="main"
        id="main-content"
        data-testid="layout__main"
        sx={{
          flexGrow: 1,
          width: isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)`,
          minHeight: '100vh',
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Header
          onMobileMenuToggle={handleMobileToggle}
          sidebarWidth={sidebarWidth}
        />

        {/* Toolbar spacer — pushes content below fixed AppBar */}
        <Box sx={{ height: { xs: 56, sm: 64 } }} />

        {/* Page content */}
        <Box
          data-testid="layout__content"
          sx={{
            flex: 1,
            p: { xs: 2, sm: 3 },
            overflow: 'auto',
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Accessibility Widget — fixed bottom-left */}
      <AccessibilityWidget />

      {/* TODO v2.0: Internal chat */}
      <div data-testid="chat-placeholder" style={{ display: 'none' }} />
    </Box>
  );
}
